import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import Modal from "@via/components/modal.tsx";
import PasswordInput from "@via/components/password-input/password-input.tsx";
import { useAdminChangePassword } from "@via/pages/account/admin/admin.api.tsx";
import { passwordValidationOptions } from "@via/utils/form-validators.tsx";
import { ChangeUserPasswordRequestBody, User } from "common";
import { useEffect, type Dispatch, type SetStateAction } from "react";
import { useForm } from "react-hook-form";

type AdminChangePasswordForm = Pick<
  ChangeUserPasswordRequestBody,
  "newPassword"
> & {
  confirmPassword: string;
};

type ChangePasswordModalProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  userToChangePassword: User | null;
  setUserToChangePassword: Dispatch<SetStateAction<User | null>>;
};

const getUserIdentity = (user: User | null) => {
  if (!user) return "this user";
  return user.email || user.userName || user.name || `user id ${user.id}`;
};

export default function ChangePasswordModal({
  open,
  setOpen,
  userToChangePassword,
  setUserToChangePassword,
}: ChangePasswordModalProps) {
  const {
    mutate: adminChangePassword,
    isPending: isChangingPassword,
    isSuccess: isChangePasswordSuccess,
  } = useAdminChangePassword();

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm<AdminChangePasswordForm>({
    mode: "onChange",
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const closeModal = () => {
    setOpen(false);
    setUserToChangePassword(null);
    reset();
  };

  useEffect(() => {
    if (isChangePasswordSuccess) {
      closeModal();
    }
  }, [isChangePasswordSuccess]);

  const handleAdminChangePassword = (formData: AdminChangePasswordForm) => {
    if (!userToChangePassword) return;
    adminChangePassword({
      ownerId: userToChangePassword.id,
      newPassword: formData.newPassword,
    });
  };

  return (
    <Modal
      open={open}
      setOpen={nextOpen => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setUserToChangePassword(null);
          reset();
        }
      }}
      title="Change password"
      showButtons={false}>
      <form
        className="w-[420px] max-w-[calc(100vw-2rem)]"
        onSubmit={e => {
          e.preventDefault();
          void handleSubmit(handleAdminChangePassword)(e);
        }}>
        <p className="mb-4 text-sm text-gray-600">
          Set a new password for{" "}
          <strong>{getUserIdentity(userToChangePassword)}</strong>.
        </p>
        <PasswordInput
          register={register}
          registerValue={"newPassword"}
          watch={watch}
          errors={errors}
        />
        <FormInputComponent
          control={control}
          type="password"
          name="confirmPassword"
          label="Confirm New Password"
          validations={{
            ...passwordValidationOptions.password,
            validate: {
              ...passwordValidationOptions.password.validate,
              matchesNewPassword: (value: string) =>
                value === watch("newPassword") || "Passwords must match",
            },
          }}
          className={"py-2"}
        />
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={closeModal}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={isChangingPassword}
            className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50">
            <Spinner loading={isChangingPassword}>Update password</Spinner>
          </button>
        </div>
      </form>
    </Modal>
  );
}
